import React, { useState, useEffect } from "react";
import { 
  Shield, 
  Settings, 
  FileText, 
  Scale, 
  CheckCircle, 
  AlertCircle, 
  CornerDownRight, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Accessibility, 
  Send, 
  UserCheck, 
  Info,
  Sliders,
  Sparkles,
  Lock
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface NexoraXLegalDocumentsProps {
  currentRoute: string;
  onNavigate: (route: string) => void;
  onShowNotice?: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export function NexoraXLegalDocuments({ currentRoute, onNavigate, onShowNotice }: NexoraXLegalDocumentsProps) {
  // Task 7: Cookie settings states
  const [cookieSettings, setCookieSettings] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_cookie_consent_v2");
      if (saved) {
        try { return JSON.parse(saved); } catch (e) {}
      }
    }
    return {
      strictlyNecessary: true, // Always true
      preferences: false,
      analytics: false,
      marketing: false,
      chatPersonalisation: false,
      firstPartyAds: false
    };
  });

  const [cookieLogs, setCookieLogs] = useState<any[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_cookie_logs");
      if (saved) {
        try { return JSON.parse(saved); } catch (e) {}
      }
    }
    return [];
  });

  // Privacy Rights Form states
  const [privacyForm, setPrivacyForm] = useState({
    fullName: "",
    email: "",
    requestType: "ACCESS", // ACCESS, RECTIFY, ERASE, PORTABILITY, RESTRICT
    details: "",
    consentChecked: false
  });

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: ""
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  // Helper to append cookie choices to standard client log
  const saveCookieConsentState = (updatedSettings: typeof cookieSettings) => {
    setCookieSettings(updatedSettings);
    if (typeof window !== "undefined") {
      localStorage.setItem("nexorax_cookie_consent_v2", JSON.stringify(updatedSettings));
      
      const session_id = sessionStorage.getItem("anonymous_session_id") || "sess_anon_" + Math.random().toString(36).substring(2, 10);
      if (!sessionStorage.getItem("anonymous_session_id")) {
        sessionStorage.setItem("anonymous_session_id", session_id);
      }

      // Generate a mock SHA256 hashed IP placeholder
      const hashed_ip = "sha256_mock_" + Math.random().toString(36).substring(2, 15);
      
      const newLog = {
        consent_id: "CONS-" + Date.now().toString(36).toUpperCase(),
        anonymous_session_id: session_id,
        user_id: "visitor@nexorax.co.uk",
        categories_accepted: Object.keys(updatedSettings).filter(k => updatedSettings[k as keyof typeof updatedSettings] === true),
        source_page: window.location.pathname || "/cookie-settings",
        ip_hash: hashed_ip,
        withdrawal_status: "Active"
      };

      const originalLogs = [...cookieLogs, newLog];
      setCookieLogs(originalLogs);
      localStorage.setItem("nexorax_cookie_logs", JSON.stringify(originalLogs));

      // Append to the accounting consent logs globally if there is any handler, 
      // otherwise save it locally so that the compliance panel can find it
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("nexorax-consent-updated", { detail: newLog }));
      }
    }

    if (onShowNotice) {
      onShowNotice("Cookie preferences synchronized and applied successfully.", "success");
    }
  };

  const handleSelectAllCookies = () => {
    const fresh = {
      strictlyNecessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
      chatPersonalisation: true,
      firstPartyAds: true
    };
    saveCookieConsentState(fresh);
  };

  const handleRejectNonEssential = () => {
    const fresh = {
      strictlyNecessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
      chatPersonalisation: false,
      firstPartyAds: false
    };
    saveCookieConsentState(fresh);
  };

  const handleSaveCookiePreferences = () => {
    saveCookieConsentState(cookieSettings);
  };

  const handlePrivacyFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!privacyForm.fullName || !privacyForm.email || !privacyForm.consentChecked) {
      if (onShowNotice) onShowNotice("Please complete all required fields and verify consent.", "error");
      return;
    }

    // Capture response in localized records
    if (typeof window !== "undefined") {
      const key = "nexorax_privacy_requests";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      const record = {
        id: "REQ-" + Math.random().toString(36).substring(3, 9).toUpperCase(),
        name: privacyForm.fullName,
        email: privacyForm.email,
        requestType: privacyForm.requestType,
        details: privacyForm.details,
        timestamp: new Date().toISOString(),
        status: "PENDING_REVIEW"
      };
      existing.push(record);
      localStorage.setItem(key, JSON.stringify(existing));
      window.dispatchEvent(new CustomEvent("nexorax-privacy-request-created", { detail: record }));
    }

    setFormSubmitted(true);
    if (onShowNotice) onShowNotice("Privacy rights request lodged successfully within legal ledger.", "success");
  };

  const handleContactFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      if (onShowNotice) onShowNotice("Please fill out Name, Email, and Message details.", "error");
      return;
    }

    if (typeof window !== "undefined") {
      const key = "nexorax_contact_logs";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      existing.push({
        ...contactForm,
        id: "CON-" + Math.random().toString(36).substring(3, 9).toUpperCase(),
        timestamp: new Date().toISOString()
      });
      localStorage.setItem(key, JSON.stringify(existing));
    }

    setFormSubmitted(true);
    if (onShowNotice) onShowNotice("Your message has been put on transmission queue for executive dispatch.", "success");
  };

  // Keep navigation in sync
  const legalRoutes = [
    { name: "Privacy Policy", path: "/privacy-policy", icon: Shield },
    { name: "Cookie Policy", path: "/cookie-policy", icon: Info },
    { name: "Terms & Conditions", path: "/terms", icon: Scale },
    { name: "Refund Policy", path: "/refund-policy", icon: FileText },
    { name: "Cancellation Policy", path: "/cancellation-policy", icon: Clock },
    { name: "Cookie Settings", path: "/cookie-settings", icon: Settings },
    { name: "Accessibility", path: "/accessibility", icon: Accessibility },
    { name: "Privacy Rights Request", path: "/privacy-rights-request", icon: UserCheck },
    { name: "Contact Gateway", path: "/contact", icon: Mail }
  ];

  return (
    <div id="nexorax-legal-documents-parent" className="w-full max-w-5xl mx-auto py-12 px-4 sm:px-6 relative text-left">
      <div className="absolute inset-0 bg-gradient-to-b from-[#3a0c5c]/10 via-transparent to-transparent pointer-events-none rounded-3xl blur-3xl"></div>

      {/* Internal Navigation Side Bar or Stacked Menu Header */}
      <div className="flex flex-wrap gap-2 justify-center mb-8 pb-4 border-b border-purple-500/10 relative z-10">
        {legalRoutes.map((routeItem) => {
          const Icon = routeItem.icon;
          const isActive = currentRoute === routeItem.path;
          return (
            <button
              key={routeItem.path}
              id={`legal-nav-btn-${routeItem.path.replace('/', '')}`}
              onClick={() => {
                setFormSubmitted(false);
                onNavigate(routeItem.path);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-mono tracking-wider uppercase border transition-all cursor-pointer ${
                isActive 
                ? "bg-purple-900/30 text-cyan-400 border-cyan-400/50 shadow-[0_0_12px_rgba(0,255,255,0.15)] font-bold scale-[1.03]" 
                : "bg-black/40 text-slate-400 border-white/5 hover:border-purple-500/30 hover:text-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {routeItem.name}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentRoute}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.3 }}
          className="glass-panel border border-purple-500/15 rounded-3xl p-6 sm:p-10 bg-[#070707]/90 backdrop-blur-md relative z-10 shadow-[0_0_40px_rgba(58,12,92,0.15)]"
        >
          {/* SECTION 1: PRIVACY POLICY */}
          {currentRoute === "/privacy-policy" && (
            <div id="legal-privacy-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Global Privacy Policy
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Shield className="w-6 h-6 animate-pulse" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  At <strong>NexoraX Digital Ltd</strong> (SC887095), digital privacy and intellectual security function as absolute priorities. 
                  Our operations are structured under direct compliance with the UK Data Protection Act (DPA 2018), General Data Protection Regulation (GDPR), 
                  and the security standards appropriate for global enterprise automation environments.
                </p>

                <div className="p-4 bg-purple-950/10 border border-purple-500/20 rounded-2xl">
                  <h3 className="font-mono font-bold text-xs uppercase text-cyan-400 mb-1.5 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    Director's Compliance Rule
                  </h3>
                  <p className="font-mono text-[10.5px] text-slate-300 leading-relaxed">
                    NexoraX may process usage events, service logs, account activity, and product interaction data to operate services, improve reliability, manage security, support billing records, and understand how Nexus products are used.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    1. Data Collected Natively
                  </h2>
                  <p>
                    NexoraX does not employ third-party advertising cookie networks or engage in first-party analytics or deep profile monetization. Data collected is strictly restricted to logical metrics necessary for operational performance:
                  </p>
                  <ul className="list-disc list-inside pl-4 space-y-2 font-mono text-[10.5px] text-slate-400">
                    <li>Account identifiers (Email coordinates, Username hash matches).</li>
                    <li>Security parameters (MFA handshake timestamps, cryptographic isolate seals).</li>
                    <li>System interaction logs (Credits allocated, transaction quantities, search completions).</li>
                    <li>Server-side request histories proxying third-party API keys securely.</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    2. Data Retention & Safeguards
                  </h2>
                  <p>
                    Your records are retained for a maximum of seven (7) years to align with HM Revenue and Customs (HMRC) auditing regulations for paid enterprise license accounting and compliance verifications. 
                    IP addresses and browser telemetry are strictly hashed using sha256 before disk allocation. To secure total compliance, no raw database secrets, server credentials, or individual payroll figures are exposed.
                  </p>
                </div>

                <div className="space-y-3">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    3. Contact Information
                  </h2>
                  <p>
                    For questions regarding your data under the UK DPA:
                    <br />
                    <strong>Data Protection Officer</strong>
                    <br />
                    NexoraX Digital Ltd // SC887095 // Dunfermline, Fife, Scotland KY11 3PA
                    <br />
                    Communication Frequency: <a href="mailto:support@nexoraxdigital.co.uk" className="text-cyan-400 hover:underline">support@nexoraxdigital.co.uk</a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 2: COOKIE POLICY */}
          {currentRoute === "/cookie-policy" && (
            <div id="legal-cookie-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Cookie Policy
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Sliders className="w-6 h-6" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  This Cookie Policy explains how NexoraX Digital Ltd uses cookie protocols, session assets, and client storage state markers to preserve stability and authorize user workflows across the Nexus workspace.
                </p>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    What are cookies?
                  </h2>
                  <p>
                    Cookies are small text structures placed onto your hardware which allow our applet to remember who is logged in, tracking active session keys safely.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    Visual Classification
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left font-mono text-[10px] text-slate-300">
                      <thead>
                        <tr className="border-b border-white/10 text-slate-500">
                          <th className="py-2">Category</th>
                          <th className="py-2">Purpose</th>
                          <th className="py-2">Default Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 font-bold text-cyan-400">Strictly Necessary</td>
                          <td className="py-2.5 text-slate-400">Authentication keys, MFA gates, security state retention.</td>
                          <td className="py-2.5 text-emerald-400">REQUIRED (Permanent)</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 font-bold">Preferences</td>
                          <td className="py-2.5 text-slate-400">Tuner layouts, theme states, search history storage.</td>
                          <td className="py-2.5 text-red-400">OPT-IN (Disabled)</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 font-bold">Analytics</td>
                          <td className="py-2.5 text-slate-400">Measuring performance bottlenecks and search API latency limits.</td>
                          <td className="py-2.5 text-red-400">OPT-IN (Disabled)</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2.5 font-bold">Marketing & Ads</td>
                          <td className="py-2.5 text-slate-400">First-party website product promotion, free trial updates.</td>
                          <td className="py-2.5 text-red-400">OPT-IN (Disabled)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="font-mono text-[10.5px] leading-relaxed text-slate-400">
                  You can audit and re-adjust consent categories dynamically at any time by visiting our dedicated <span onClick={() => onNavigate("/cookie-settings")} className="text-cyan-400 underline hover:text-cyan-200 cursor-pointer">Cookie Settings Panel</span>. No tracking elements are launched before consent is explicitly provided.
                </p>
              </div>
            </div>
          )}

          {/* SECTION 3: TERMS & CONDITIONS */}
          {currentRoute === "/terms" && (
            <div id="legal-terms-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Terms of Service
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Scale className="w-6 h-6" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  Welcome to the <strong>NexoraX Platform</strong>. These terms govern your access and use of our enterprise workspaces under the legal codes of Scotland, United Kingdom.
                </p>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    1. Managed Infrastructure Fees
                  </h2>
                  <p>
                    A standard <strong>10% Maintenance & Hosting Fee</strong> applies to all active deployment operations, AI-automation workflows, website database systems, and integration pipelines provided by NexoraX. 
                    This fee applies strictly while the active service is hosted, operated, or provided. It is immediately stopped if the service is cancelled, terminated, or no longer actively provided.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    2. Regional Community Foundation
                  </h2>
                  <p>
                    NexoraX Digital Ltd is dedicated to local community development. <strong>3% of net licensing proceeds</strong> from our enterprise contracts is permanently allocated to local digital literacy initiatives in Dunfermline, Fife, and regional Scotland community hubs, managed as split-allocations.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    3. Acceptable Use
                  </h2>
                  <p>
                    A strict <strong>150 session search credit limit</strong> applies to standard client previews to protect systemic cloud resources. Any scraping of raw intelligence databases, replication of source code layers, or security posture violations will trigger immediate session key destruction and persistent IP defense blocking.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 4: REFUND POLICY */}
          {currentRoute === "/refund-policy" && (
            <div id="legal-refund-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-[#fe3a94]/20 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Refund Policy
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-[#fe3a94]/20 bg-[#fe3a94]/10 rounded-xl text-[#fe3a94]">
                  <FileText className="w-6 h-6" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  At NexoraX, we enforce highly predictable, enterprise-grade invoicing structures. All paid allocations and licensing configurations are flat-rate.
                </p>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#fe3a94] pl-3.5">
                    Enterprise Setup & Deployment
                  </h2>
                  <p>
                    Upon purchase, custom cloud environments are compiled natively for immediate deployment. Initial setup and code deployment work fees are <strong>strictly non-refundable</strong> once infrastructure initialization has been triggered.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#fe3a94] pl-3.5">
                    Subscriptions & Recurrent Licenses
                  </h2>
                  <p>
                    Recurrent workspace subscription items are active for 30-day blocks. Partial refunds for middle-cycle cancellation are not issued. If a billing error or double-charge is highlighted, contact the Executive Team within 15 days at <a href="mailto:info@nexoraxdigital.co.uk" className="text-cyan-400 hover:underline">info@nexoraxdigital.co.uk</a> to run manual transaction verification.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 5: CANCELLATION POLICY */}
          {currentRoute === "/cancellation-policy" && (
            <div id="legal-cancellation-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Cancellation Policy
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Clock className="w-6 h-6" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  NexoraX workspace operations strive to deliver zero-friction exit options for corporate clients. Subscriptions can be canceled at any time using your core dashboard settings.
                </p>

                <div className="p-4 bg-zinc-950/80 border border-white/5 rounded-2xl">
                  <h3 className="font-mono font-bold text-xs uppercase text-amber-400 mb-1.5 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    Notice Requirement
                  </h3>
                  <p className="font-mono text-[10.5px] text-slate-300 leading-relaxed">
                    To prevent automated workspace renewals billing, standard contracts require a minimum of <strong>30 days written notice</strong> prior to the renewal date. All active workloads remain fully authorized during the notice span.
                  </p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-black font-mono uppercase text-white tracking-widest border-l-2 border-[#00ffd2] pl-3.5">
                    Effect on Managed Infrastructure Support
                  </h2>
                  <p>
                    Upon active corporate contract cancellation, your dedicated Google Workspace provisioning, domain routing indices, and 10% Maintenance & Hosting Fees are completely phased out at the next contract boundary. No residual charges apply once services are inactive.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 6: COOKIE SETTINGS (Task 7 integration) */}
          {currentRoute === "/cookie-settings" && (
            <div id="legal-cookiesettings-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Cookie Settings Center
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Manage choices natively // Saves immediately
                  </span>
                </div>
                <div className="p-2 border border-[#00ffd2]/20 bg-[#00ffd2]/10 rounded-xl text-[#00ffd2]">
                  <Settings className="w-6 h-6 animate-spin-slow" />
                </div>
              </div>

              <div className="space-y-6">
                <p className="font-sans text-xs text-slate-300">
                  Audit and adjust user privacy parameters before server-side event pipeline validation. Only essential categories are active before explicit input choices.
                </p>

                {/* Accept All and Reject Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    id="cookies-accept-all-btn"
                    onClick={handleSelectAllCookies}
                    className="flex-1 px-4 py-2.5 hover:opacity-90 bg-gradient-to-r from-purple-500 to-cyan-400 text-black font-mono text-[10px] uppercase font-black rounded-xl cursor-pointer active:scale-97 transition"
                  >
                    🚀 Accept All Cookies
                  </button>
                  <button
                    id="cookies-reject-all-btn"
                    onClick={handleRejectNonEssential}
                    className="flex-1 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-white/5 hover:border-white/10 text-white font-mono text-[10px] uppercase font-bold rounded-xl cursor-pointer active:scale-97 transition"
                  >
                    🚫 Reject Non-Essential
                  </button>
                </div>

                <div className="border border-white/5 rounded-2xl divide-y divide-white/5 overflow-hidden">
                  {/* Category 1 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-white uppercase">Strictly Necessary Cookies</span>
                        <span className="px-2 py-0.5 rounded bg-emerald-950 border border-emerald-500/30 text-[8px] font-mono text-emerald-400 uppercase font-black">Always On</span>
                      </div>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Required to verify identity keys, handle security boundaries, authorize logins, and store token handshakes. Cannot be deactivated.
                      </p>
                    </div>
                    <div>
                      <div className="w-10 h-6 bg-purple-900 border border-purple-500 rounded-full flex items-center px-1 py-1 cursor-default opacity-50 relative">
                        <div className="w-4 h-4 bg-cyan-400 rounded-full translate-x-4"></div>
                      </div>
                    </div>
                  </div>

                  {/* Category 2 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <span className="font-mono text-xs font-bold text-white uppercase">Personalization & Preferences</span>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Saves layout states, grid densities, and dashboard theme configurations across active client nodes.
                      </p>
                    </div>
                    <button
                      id="opt-cookie-preferences"
                      onClick={() => setCookieSettings(prev => ({ ...prev, preferences: !prev.preferences }))}
                      className="cursor-pointer"
                    >
                      <div className={`w-10 h-6 border rounded-full flex items-center px-1 py-1 transition-colors ${cookieSettings.preferences ? 'bg-purple-900 border-purple-500' : 'bg-zinc-950 border-zinc-700'}`}>
                        <div className={`w-4 h-4 bg-cyan-400 rounded-full transition-transform ${cookieSettings.preferences ? 'translate-x-4' : 'translate-x-0 bg-slate-500'}`}></div>
                      </div>
                    </button>
                  </div>

                  {/* Category 3 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <span className="font-mono text-xs font-bold text-white uppercase">Performance & Analytics</span>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Captures usage events, service logs, account activity, and product interaction data to check system load, latency, and operational performance.
                      </p>
                    </div>
                    <button
                      id="opt-cookie-analytics"
                      onClick={() => setCookieSettings(prev => ({ ...prev, analytics: !prev.analytics }))}
                      className="cursor-pointer"
                    >
                      <div className={`w-10 h-6 border rounded-full flex items-center px-1 py-1 transition-colors ${cookieSettings.analytics ? 'bg-purple-900 border-purple-500' : 'bg-zinc-950 border-zinc-700'}`}>
                        <div className={`w-4 h-4 bg-cyan-400 rounded-full transition-transform ${cookieSettings.analytics ? 'translate-x-4' : 'translate-x-0 bg-slate-500'}`}></div>
                      </div>
                    </button>
                  </div>

                  {/* Category 4 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <span className="font-mono text-xs font-bold text-white uppercase">Marketing & Promo-Tracking</span>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Tracks promotional campaigns and educational video watch state inside active user previews.
                      </p>
                    </div>
                    <button
                      id="opt-cookie-marketing"
                      onClick={() => setCookieSettings(prev => ({ ...prev, marketing: !prev.marketing }))}
                      className="cursor-pointer"
                    >
                      <div className={`w-10 h-6 border rounded-full flex items-center px-1 py-1 transition-colors ${cookieSettings.marketing ? 'bg-purple-900 border-purple-500' : 'bg-zinc-950 border-zinc-700'}`}>
                        <div className={`w-4 h-4 bg-cyan-400 rounded-full transition-transform ${cookieSettings.marketing ? 'translate-x-4' : 'translate-x-0 bg-slate-500'}`}></div>
                      </div>
                    </button>
                  </div>

                  {/* Category 5 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <span className="font-mono text-xs font-bold text-white uppercase">Chat Personalisation</span>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Secures active conversation logs with the neural assistant node, remembering client context for fast-track queries.
                      </p>
                    </div>
                    <button
                      id="opt-cookie-chat"
                      onClick={() => setCookieSettings(prev => ({ ...prev, chatPersonalisation: !prev.chatPersonalisation }))}
                      className="cursor-pointer"
                    >
                      <div className={`w-10 h-6 border rounded-full flex items-center px-1 py-1 transition-colors ${cookieSettings.chatPersonalisation ? 'bg-purple-900 border-purple-500' : 'bg-zinc-950 border-zinc-700'}`}>
                        <div className={`w-4 h-4 bg-cyan-400 rounded-full transition-transform ${cookieSettings.chatPersonalisation ? 'translate-x-4' : 'translate-x-0 bg-slate-500'}`}></div>
                      </div>
                    </button>
                  </div>

                  {/* Category 6 */}
                  <div className="p-4 bg-black/40 flex items-center justify-between gap-3">
                    <div className="space-y-1 max-w-sm sm:max-w-md">
                      <span className="font-mono text-xs font-bold text-white uppercase">First-Party Website Ads</span>
                      <p className="text-[10px] text-zinc-400 leading-relaxed">
                        Aggregates anonymous website interactions to suggest additional NexoraX modules relevant to your sector.
                      </p>
                    </div>
                    <button
                      id="opt-cookie-firstparty"
                      onClick={() => setCookieSettings(prev => ({ ...prev, firstPartyAds: !prev.firstPartyAds }))}
                      className="cursor-pointer"
                    >
                      <div className={`w-10 h-6 border rounded-full flex items-center px-1 py-1 transition-colors ${cookieSettings.firstPartyAds ? 'bg-purple-900 border-purple-500' : 'bg-zinc-950 border-zinc-700'}`}>
                        <div className={`w-4 h-4 bg-cyan-400 rounded-full transition-transform ${cookieSettings.firstPartyAds ? 'translate-x-4' : 'translate-x-0 bg-slate-500'}`}></div>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    id="save-cookie-choices-btn"
                    onClick={handleSaveCookiePreferences}
                    className="px-5 py-2 hover:opacity-90 bg-zinc-950 border border-[#00ffd2]/40 text-[#00ffd2] font-mono text-[10px] uppercase font-bold rounded-xl active:scale-95 transition cursor-pointer"
                  >
                    💾 Save Preferences & Create Consent Log
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 7: ACCESSIBILITY STATEMENT */}
          {currentRoute === "/accessibility" && (
            <div id="legal-accessibility-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Accessibility Standard
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Last Updated: June 15, 2026 // NexoraX Digital Compliance
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Accessibility className="w-6 h-6 animate-bounce" />
                </div>
              </div>

              <div className="font-sans text-xs text-slate-300 leading-relaxed space-y-6">
                <p>
                  At <strong>NexoraX Digital Ltd</strong>, we strive to maximize digital equality across our platforms. We design our client dashboards and public elements to run smoothly across varying capabilities.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-950 rounded-xl border border-white/5 space-y-2">
                    <span className="font-mono text-[10px] font-bold text-cyan-400 uppercase">Visual Contrast Ratio</span>
                    <p className="text-[11px] text-zinc-400">
                      Standard text exhibits a 4.5:1 ratio against the deep obsidian canvas. Purple glassmorphic highlights are strictly formatted to prevent user fatigue.
                    </p>
                  </div>
                  <div className="p-4 bg-zinc-950 rounded-xl border border-white/5 space-y-2">
                    <span className="font-mono text-[10px] font-bold text-cyan-400 uppercase">Interactive Elements</span>
                    <p className="text-[11px] text-zinc-400">
                      All buttons and text inputs utilize standardized <code>id</code> attributes, clear tooltips, and standard keyboard focus transitions.
                    </p>
                  </div>
                </div>

                <p className="font-mono text-[10.5px] text-slate-400">
                  If you require any assistance and encounter localized digital friction, please reach out to our team immediately at <strong className="text-white">accessibility@nexorax.co.uk</strong>.
                </p>
              </div>
            </div>
          )}

          {/* SECTION 8: GDPR/UK DPA DATA RIGHTS PORTAL */}
          {currentRoute === "/privacy-rights-request" && (
            <div id="legal-datarights-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Privacy Rights Request Portal
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    UK Data Protection Act 2018 // Submit Request
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <UserCheck className="w-6 h-6" />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {formSubmitted ? (
                  <motion.div
                    key="submitted-success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-8 text-center bg-purple-950/10 border border-purple-500/20 rounded-2xl space-y-3"
                  >
                    <div className="inline-flex p-3 bg-cyan-950/30 border border-cyan-400/40 rounded-full text-cyan-400 text-3xl mb-1">
                      ✓
                    </div>
                    <h3 className="font-sans font-black text-white text-base uppercase tracking-tight">
                      Handshake Logged Successfully
                    </h3>
                    <p className="font-mono text-[10.5px] text-slate-400 max-w-md mx-auto">
                      Your legal request record was securely appended to the Google Drive NexoraX Resource Library under reference code.
                    </p>
                    <button
                      onClick={() => setFormSubmitted(false)}
                      className="px-4 py-2 mt-4 bg-zinc-900 border border-white/10 hover:border-white/20 text-white font-mono text-[10px] uppercase rounded-lg cursor-pointer"
                    >
                      Submit Another
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handlePrivacyFormSubmit} id="privacy-rights-form" className="space-y-4 font-mono text-[11px]">
                    <div className="p-4 bg-zinc-950/80 border border-white/5 rounded-2xl text-[10.5px] text-zinc-400 leading-relaxed">
                      🔒 <strong>Isolation Safeguard:</strong> This form operates strictly on a double-blind transport. Your credentials are matched against verified corporate logins securely. No raw logs, bank details, or secrets are exposed.
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-zinc-500 font-bold uppercase block text-[10px]">Full Name</label>
                        <input
                          type="text"
                          required
                          value={privacyForm.fullName}
                          onChange={e => setPrivacyForm(prev => ({ ...prev, fullName: e.target.value }))}
                          placeholder="Lord Mahmood"
                          className="w-full bg-black border border-white/10 focus:border-[#00ffd2] focus:outline-none px-3 py-2 text-white rounded-xl font-sans"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-zinc-500 font-bold uppercase block text-[10px]">Email Coordinates</label>
                        <input
                          type="email"
                          required
                          value={privacyForm.email}
                          onChange={e => setPrivacyForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="client@nexorax.co.uk"
                          className="w-full bg-black border border-white/10 focus:border-[#00ffd2] focus:outline-none px-3 py-2 text-white rounded-xl font-sans"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-zinc-500 font-bold uppercase block text-[10px]">Request Category</label>
                      <select
                        value={privacyForm.requestType}
                        onChange={e => setPrivacyForm(prev => ({ ...prev, requestType: e.target.value }))}
                        className="w-full bg-black border border-white/10 focus:border-[#00ffd2] px-3 py-2 text-white rounded-xl focus:outline-none"
                      >
                        <option value="ACCESS">ACCESS: Retrieve my data ledger</option>
                        <option value="RECTIFY">RECTIFY: Correct record inaccuracies</option>
                        <option value="ERASE">ERASE: Trigger compliance erasure</option>
                        <option value="PORTABILITY">PORTABILITY: Request standard JSON export</option>
                        <option value="RESTRICT">RESTRICT: Opt-out of non-essential checks</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-zinc-500 font-bold uppercase block text-[10px]">Specific Instructions</label>
                      <textarea
                        rows={3}
                        value={privacyForm.details}
                        onChange={e => setPrivacyForm(prev => ({ ...prev, details: e.target.value }))}
                        placeholder="Verify retention span allocations..."
                        className="w-full bg-black border border-white/10 focus:border-[#00ffd2] focus:outline-none px-3 py-2 text-white rounded-xl font-sans"
                      />
                    </div>

                    <label className="flex items-start gap-2 pt-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={privacyForm.consentChecked}
                        onChange={e => setPrivacyForm(prev => ({ ...prev, consentChecked: e.target.checked }))}
                        className="mt-1 accent-[#00ffd2] cursor-pointer"
                      />
                      <span className="text-[10px] text-zinc-400 group-hover:text-zinc-300 transition-colors">
                        I authorize NexoraX Digital Ltd to parse email coordinates solely to audit corresponding internal ledgers under standard UK Data Protection guidelines.
                      </span>
                    </label>

                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-400 text-black font-extrabold rounded-xl uppercase hover:opacity-90 active:scale-97 transition flex items-center gap-1.5 cursor-pointer"
                      >
                        <Send className="w-4 h-4" />
                        File Request
                      </button>
                    </div>
                  </form>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* SECTION 9: CONTACT */}
          {currentRoute === "/contact" && (
            <div id="legal-contact-view" className="space-y-6">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black font-sans uppercase tracking-tight text-white mb-1.5">
                    Contact Gateway
                  </h1>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    Direct executive communications // Freephone support active
                  </span>
                </div>
                <div className="p-2 border border-purple-500/20 bg-purple-950/20 rounded-xl text-purple-400">
                  <Mail className="w-6 h-6 animate-pulse" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Coordinates */}
                <div className="space-y-6">
                  <p className="font-sans text-xs text-slate-300 leading-relaxed">
                    Connect directly with our operations room. Executive support is active 24/7 to handle billing setup queries, platform integrations, or credential questions.
                  </p>

                  <div className="space-y-4 font-mono text-[11px] text-slate-300">
                    <div className="flex items-center gap-3 p-3 bg-zinc-950/80 border border-white/5 rounded-xl">
                      <Phone className="w-4 h-4 text-[#00ffd2]" />
                      <div>
                        <span className="text-zinc-500 block text-[9px] uppercase">Verbal Negotiations</span>
                        <a href="tel:08000 622827" className="font-sans font-bold text-white hover:text-[#00ffd2]">08000 622827 (Freephone)</a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-zinc-950/80 border border-white/5 rounded-xl">
                      <Mail className="w-4 h-4 text-[#00ffd2]" />
                      <div>
                        <span className="text-zinc-500 block text-[9px] uppercase">Corporate Dispatch Email</span>
                        <a href="mailto:info@nexoraxdigital.co.uk" className="font-sans font-bold text-white hover:text-[#00ffd2]">info@nexoraxdigital.co.uk</a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-zinc-950/80 border border-white/5 rounded-xl">
                      <MapPin className="w-4 h-4 text-[#00ffd2]" />
                      <div>
                        <span className="text-zinc-500 block text-[9px] uppercase">Corporate H.Q. Coordinates</span>
                        <span className="font-sans font-bold text-white">Dunfermline, Fife, Scotland, KY11 3PA</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secure Contact Form */}
                <div className="glass-panel border border-white/5 bg-black/40 rounded-2xl p-6">
                  <AnimatePresence mode="wait">
                    {formSubmitted ? (
                      <motion.div
                        key="contact-success"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-8 space-y-3"
                      >
                        <div className="text-3xl">🚀</div>
                        <h4 className="font-sans font-bold text-white text-sm uppercase">Message Transmitted</h4>
                        <p className="font-mono text-[10px] text-zinc-400">
                          Secure dispatch logged immediately. Our general coordinator will contact you shortly.
                        </p>
                        <button
                          onClick={() => setFormSubmitted(false)}
                          className="px-3 py-1.5 mt-2 bg-zinc-900 border border-white/10 hover:border-white/20 text-white font-mono text-[9px] uppercase rounded-lg"
                        >
                          Send Again
                        </button>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleContactFormSubmit} className="space-y-4 font-mono text-[10.5px]">
                        <div className="space-y-1">
                          <label className="text-zinc-500 block uppercase">Your Name</label>
                          <input
                            type="text"
                            required
                            value={contactForm.name}
                            onChange={e => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full bg-black border border-white/10 focus:border-[#00ffd2] px-3 py-2 text-white rounded-xl focus:outline-none"
                            placeholder="Director Director"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-zinc-500 block uppercase">Email Coordinates</label>
                          <input
                            type="email"
                            required
                            value={contactForm.email}
                            onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full bg-black border border-white/10 focus:border-[#00ffd2] px-3 py-2 text-white rounded-xl focus:outline-none"
                            placeholder="support@nexoraxdigital.co.uk"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-zinc-500 block uppercase">Message Field</label>
                          <textarea
                            required
                            rows={3}
                            value={contactForm.message}
                            onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                            className="w-full bg-black border border-white/10 focus:border-[#00ffd2] px-3 py-2 text-white rounded-xl focus:outline-none"
                            placeholder="Type corporate communication here..."
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full py-2 bg-purple-900/40 hover:bg-purple-800 border border-purple-500/30 hover:border-[#00ffd2] text-white font-bold rounded-xl uppercase transition active:scale-97 cursor-pointer"
                        >
                          Transmit Dispatch
                        </button>
                      </form>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
